// src/components/commercial/AmbassadorProspects.jsx
//
// Admin page: the ambassador prospect pool (SIRENE import + places added in
// the field). Search, the "to review" queue (added by ambassadors, suspected
// duplicates), who holds what, and the actions: free a stuck reservation,
// do-not-contact, accept, merge a duplicate. Backed by
// /admin/commercial/ambassador/prospects (backend/routes/commercial/ambassadorAdmin.ts).
// Unlike the ambassador app, ambassadors' names are shown here.
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Alert, TextField, MenuItem, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Pagination, Link,
} from '@mui/material';
import useCommercialAdmin from '../../utils/useCommercialAdmin';
import { commercialGet, commercialPost, commercialPut } from '../../utils/commercialApi';

const STATUSES = ['PROSPECT', 'CONTACTED', 'DEMO', 'TRIAL', 'ACTIVATED', 'PAID', 'LOST', 'CHURNED'];
const SOURCES = { SIRENE: 'Import SIRENE', AMBASSADOR: 'Ajouté par un ambassadeur', MANUAL: 'Saisie manuelle', REFERRAL: 'Parrainage' };
const REVIEW = { PENDING_REVIEW: 'À valider', DUPLICATE_SUSPECTED: 'Doublon possible' };
const DNC_REASONS = { CLOSED: 'Fermé', REQUESTED: 'A demandé à ne plus être contacté', STRATEGIC: 'Compte géré en direct (caché aux ambassadeurs)', OTHER: 'Autre' };
const RESULTS = {
  OWNER_ABSENT: 'Gérant absent', CALL_BACK: 'À rappeler', INTERESTED: 'Intéressé', REFUSED: 'Refus',
  INSTALLATION_ACCEPTED: 'Installation acceptée', CLOSED: 'Fermé', BAD_RECORD: 'Fiche erronée', OTHER: 'Autre',
};
const GPS = { OK: 'OK', WARNING: 'À surveiller', REVIEW: 'À vérifier' };
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '-');

export default function AmbassadorProspects() {
  const { ready, forbidden, loading: authLoading, getToken } = useCommercialAdmin();
  const [params, setParams] = useSearchParams();
  const [territories, setTerritories] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState(params.get('q') ?? '');

  const [detail, setDetail] = useState(null);       // GET /prospects/:id
  const [dnc, setDnc] = useState({ open: false, reason: 'CLOSED' });
  const [mergeTarget, setMergeTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const filter = (name) => params.get(name) ?? '';
  const setFilter = (name, value) => {
    const next = new URLSearchParams(params);
    if (value === '' || value == null) next.delete(name); else next.set(name, value);
    if (name !== 'page') next.delete('page');
    setParams(next);
  };

  const load = useCallback(() => {
    if (!ready) return;
    setData(null);
    commercialGet('/ambassador/prospects', getToken(), Object.fromEntries(params.entries()))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [ready, getToken, params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!ready) return;
    commercialGet('/ambassador/territories', getToken()).then((d) => setTerritories(d.territories)).catch(() => {});
  }, [ready, getToken]);

  const open = async (id) => {
    setError('');
    try { setDetail(await commercialGet(`/ambassador/prospects/${id}`, getToken())); } catch (e) { setError(e.message); }
  };
  const act = async (fn) => {
    setBusy(true); setError('');
    try {
      await fn();
      const id = detail?.prospect?.id;
      if (id) await open(id);
      load();
    } catch (e) {
      setError({
        SOURCE_IS_CUSTOMER: 'Ce prospect est déjà client : fusionnez l’autre fiche dans celle-ci.',
        SOURCE_RESERVED: 'Ce prospect est réservé : libérez d’abord la réservation.',
        PROSPECT_NOT_FOUND: 'Prospect introuvable (vérifiez le numéro de la fiche cible).',
        NO_ACTIVE_RESERVATION: 'Aucune réservation en cours.',
      }[e.message] ?? e.message);
    } finally { setBusy(false); }
  };

  if (authLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Accès réservé aux administrateurs.</Alert></Container>;

  const p = detail?.prospect;
  const held = p && p.reserved_until && new Date(p.reserved_until) > new Date();

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Prospects des ambassadeurs</Typography>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to="/admin/commercial/ambassador/territories">Territoires</Button>
          <Button component={RouterLink} to="/admin/commercial/ambassador/visits">Visites à vérifier</Button>
          <Button component={RouterLink} to="/admin/commercial">← Dashboard</Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField size="small" label="Nom, adresse, SIRET ou téléphone" value={q} sx={{ minWidth: 280 }}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') setFilter('q', q.trim()); }} />
          <TextField select size="small" label="À traiter" value={filter('review')} sx={{ minWidth: 160 }} onChange={(e) => setFilter('review', e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            {Object.entries(REVIEW).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Statut" value={filter('status')} sx={{ minWidth: 140 }} onChange={(e) => setFilter('status', e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Territoire" value={filter('territoryId')} sx={{ minWidth: 160 }} onChange={(e) => setFilter('territoryId', e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="none">Sans territoire</MenuItem>
            {territories.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Source" value={filter('source')} sx={{ minWidth: 150 }} onChange={(e) => setFilter('source', e.target.value)}>
            <MenuItem value="">Toutes</MenuItem>
            {Object.entries(SOURCES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="En cours" value={filter('hold')} sx={{ minWidth: 150 }} onChange={(e) => setFilter('hold', e.target.value)}>
            <MenuItem value="">-</MenuItem>
            <MenuItem value="reserved">Réservés</MenuItem>
            <MenuItem value="follow_up">En relance</MenuItem>
            <MenuItem value="do_not_contact">Ne pas contacter</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        {!data && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        {data && (
          <>
            <Typography variant="body2" sx={{ p: 1.5 }}>{data.total} prospect(s)</Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Établissement</TableCell><TableCell>Territoire</TableCell><TableCell>Statut</TableCell>
                    <TableCell>Ambassadeur</TableCell><TableCell align="right">Visites</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.prospects.map((r) => (
                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{[r.address, r.postalCode].filter(Boolean).join(' ')} · #{r.id}</Typography>
                      </TableCell>
                      <TableCell>{r.territory ?? '-'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={r.facilityId ? 'Client' : r.status} />
                          {REVIEW[r.reviewStatus] && <Chip size="small" color="warning" label={REVIEW[r.reviewStatus]} />}
                          {r.doNotContact && <Chip size="small" color="error" variant="outlined" label={DNC_REASONS[r.doNotContactReason] ?? 'Ne pas contacter'} />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {r.reservedBy && <Typography variant="body2">Réservé par {r.reservedBy}</Typography>}
                        {r.followUpBy && <Typography variant="body2">Relance : {r.followUpBy}</Typography>}
                        {!r.reservedBy && !r.followUpBy && '-'}
                      </TableCell>
                      <TableCell align="right">{r.visits}</TableCell>
                      <TableCell><Button size="small">Ouvrir</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {data.total > data.pageSize && (
              <Pagination sx={{ p: 1.5 }} page={data.page + 1} count={Math.ceil(data.total / data.pageSize)}
                onChange={(_e, page) => setFilter('page', String(page - 1))} />
            )}
          </>
        )}
      </Paper>

      {/* Detail */}
      <Dialog open={!!detail} onClose={() => { setDetail(null); setMergeTarget(''); }} maxWidth="md" fullWidth>
        {p && (
          <>
            <DialogTitle>{p.business_name} <Typography component="span" color="text.secondary">#{p.id}</Typography></DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2">{[p.address, p.postal_code, p.city].filter(Boolean).join(' ')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {SOURCES[p.source] ?? p.source} · SIRET {p.siret ?? '-'} · {p.facility_type ?? '-'} · tél. {p.contact_phone ?? '-'}
                {p.territory_name ? ` · ${p.territory_name}` : ''}
              </Typography>
              {p.latitude && (
                <Link href={`https://www.openstreetmap.org/?mlat=${p.latitude}&mlon=${p.longitude}#map=19/${p.latitude}/${p.longitude}`}
                  target="_blank" rel="noreferrer" variant="body2">Voir sur la carte</Link>
              )}
              <Stack direction="row" spacing={1} sx={{ my: 2 }} flexWrap="wrap" useFlexGap>
                {held && <Button size="small" variant="outlined" disabled={busy}
                  onClick={() => act(() => commercialPost(`/ambassador/prospects/${p.id}/release`, getToken(), { reason: 'libéré par un admin' }))}>
                  Libérer la réservation</Button>}
                {p.review_status !== 'OK' && <Button size="small" variant="outlined" color="success" disabled={busy}
                  onClick={() => act(() => commercialPut(`/ambassador/prospects/${p.id}/review`, getToken(), { action: 'accept' }))}>
                  Valider la fiche</Button>}
                {Number(p.do_not_contact) === 1
                  ? <Button size="small" variant="outlined" disabled={busy}
                    onClick={() => act(() => commercialPut(`/ambassador/prospects/${p.id}/do-not-contact`, getToken(), { doNotContact: false }))}>
                    Autoriser à nouveau le contact</Button>
                  : <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => setDnc({ open: true, reason: 'CLOSED' })}>
                    Ne pas contacter…</Button>}
              </Stack>
              {!p.facility_id && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <TextField size="small" label="Fusionner dans la fiche n°" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value.replace(/\D/g, ''))} />
                  <Button size="small" variant="outlined" disabled={busy || !mergeTarget}
                    onClick={() => {
                      if (!window.confirm(`Fusionner #${p.id} dans #${mergeTarget} ? Cette fiche sera supprimée, son historique déplacé.`)) return;
                      const target = Number(mergeTarget);
                      act(async () => { await commercialPost(`/ambassador/prospects/${p.id}/merge`, getToken(), { targetId: target }); setMergeTarget(''); await open(target); });
                    }}>Fusionner</Button>
                </Stack>
              )}

              <Typography variant="subtitle2" sx={{ mt: 1 }}>Visites</Typography>
              {detail.visits.length === 0 && <Typography variant="body2" color="text.secondary">Aucune.</Typography>}
              {detail.visits.map((v) => (
                <Typography key={v.id} variant="body2">
                  {fmt(v.checkin_at)} · {v.agent} · GPS {GPS[v.gps_check]}{v.distance_m != null ? ` (${v.distance_m} m)` : ' (pas de position)'}
                  {' · '}{RESULTS[v.result] ?? 'sans résultat'}{v.comment ? ` - « ${v.comment} »` : ''}
                  {v.review_outcome ? ` · revue : ${v.review_outcome === 'ACCEPTED' ? 'acceptée' : 'rejetée'}` : ''}
                </Typography>
              ))}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Réservations</Typography>
              {detail.reservations.length === 0 && <Typography variant="body2" color="text.secondary">Aucune.</Typography>}
              {detail.reservations.map((r) => (
                <Typography key={r.id} variant="body2">
                  {fmt(r.reserved_at)} · {r.agent} · {r.status}{r.extensions ? ` · prolongée` : ''}{r.release_reason ? ` · ${r.release_reason}` : ''}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions><Button onClick={() => setDetail(null)}>Fermer</Button></DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={dnc.open} onClose={() => setDnc({ ...dnc, open: false })}>
        <DialogTitle>Ne plus contacter cet établissement</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Les réservations et relances en cours sont annulées.</Typography>
          <TextField select fullWidth size="small" label="Raison" value={dnc.reason} onChange={(e) => setDnc({ ...dnc, reason: e.target.value })}>
            {Object.entries(DNC_REASONS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDnc({ ...dnc, open: false })}>Annuler</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={() => {
            setDnc({ ...dnc, open: false });
            act(() => commercialPut(`/ambassador/prospects/${p.id}/do-not-contact`, getToken(), { doNotContact: true, reason: dnc.reason }));
          }}>Confirmer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
